"use client";

import { useState } from "react";
import { Button, Field, Input, Stack, Text, VStack } from "@chakra-ui/react";
import type { Version } from "@/generated/fastapi";
import type { ActiveProfileVersion } from "@/state/globalStoreSlices/userChoices";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSegment from "@/shared/ui/controls/GenericSegment";
import SelectionSummary from "./SelectionSummary";
import VersionSelector from "./VersionSelector";
import type { VersionSelectorItem } from "./types";

export type SaveVersionAction = "create" | "replace" | "extend";

type SaveVersionSectionProps = {
  activeProfileVersion: ActiveProfileVersion | null;
  canCreateVersion: boolean;
  canExtendVersion: boolean;
  canSaveVersion: boolean;
  canUseSaveTargetVersion: boolean;
  extendPending: boolean;
  freeListThoughtCount: number;
  hasSelectedSuggestions: boolean;
  initialAction?: SaveVersionAction;
  onClose?: () => void;
  onCreateVersion: () => Promise<void>;
  onExtendVersion: () => Promise<void>;
  onUpdateVersion: () => Promise<void>;
  printLanguage: string;
  profileFocusName?: string | null;
  savePending: boolean;
  selectedSaveTargetVersion: Version | null;
  selectedSaveTargetVersions: Version[];
  selectedThoughtCount: number;
  selectedSituationCount: number;
  setSelectedSaveTargetVersions: (versions: Version[]) => void;
  selectorEpoch: number;
  updatePending: boolean;
  versionItems: VersionSelectorItem[];
  versionName: string;
  setVersionName: (name: string) => void;
  versions: Version[];
};

export default function SaveVersionSection({
  activeProfileVersion,
  canCreateVersion,
  canExtendVersion,
  canSaveVersion,
  canUseSaveTargetVersion,
  extendPending,
  freeListThoughtCount,
  hasSelectedSuggestions,
  initialAction = "create",
  onClose,
  onCreateVersion,
  onExtendVersion,
  onUpdateVersion,
  printLanguage,
  profileFocusName,
  savePending,
  selectedSaveTargetVersion,
  selectedSaveTargetVersions,
  selectedThoughtCount,
  selectedSituationCount,
  setSelectedSaveTargetVersions,
  updatePending,
  versionName,
  setVersionName,
  selectorEpoch,
  versionItems,
  versions,
}: SaveVersionSectionProps) {
  const [saveAction, setSaveAction] =
    useState<SaveVersionAction>(initialAction);
  const [hasAttemptedCreate, setHasAttemptedCreate] = useState(false);
  const [hasAttemptedReplace, setHasAttemptedReplace] = useState(false);
  const [hasAttemptedExtend, setHasAttemptedExtend] = useState(false);
  const hasVersions = versions.length > 0;
  const activeSaveAction: SaveVersionAction =
    hasVersions || saveAction === "create" ? saveAction : "create";
  const activeSaveActionLabel =
    activeSaveAction === "create"
      ? "New"
      : activeSaveAction === "replace"
        ? "Replace"
        : "Extend";
  const trimmedVersionName = versionName.trim();
  const hasVersionName = trimmedVersionName.length > 0;
  const showVersionNameError =
    activeSaveAction === "create" &&
    canSaveVersion &&
    hasAttemptedCreate &&
    !hasVersionName;
  const showReplaceVersionError =
    activeSaveAction === "replace" &&
    canSaveVersion &&
    hasAttemptedReplace &&
    hasVersions &&
    !canUseSaveTargetVersion;
  const showReplaceSelectionError =
    activeSaveAction === "replace" && hasVersions && !canSaveVersion;
  const showExtendVersionError =
    activeSaveAction === "extend" &&
    hasAttemptedExtend &&
    hasVersions &&
    !selectedSaveTargetVersion;
  const showExtendSelectionError =
    activeSaveAction === "extend" && hasVersions && !canSaveVersion;
  const isReplaceAction = activeSaveAction === "replace";
  const isExtendAction = activeSaveAction === "extend";
  const targetVersionTitle = isReplaceAction
    ? "Update existing version"
    : "Extend existing version";
  const targetVersionDescription = isReplaceAction
    ? "Replace the saved version with the current confirmed Studio selection."
    : "Add the current confirmed Studio selection into a saved version. Existing saved material stays in place.";
  const targetVersionLabel = isReplaceAction
    ? "Version to replace"
    : "Version to extend";
  const targetVersionPlaceholder = isReplaceAction
    ? "Choose a version to update"
    : "Choose a version to extend";
  const showTargetSelectionError = isReplaceAction
    ? showReplaceSelectionError
    : showExtendSelectionError;
  const showTargetVersionError = isReplaceAction
    ? showReplaceVersionError
    : showExtendVersionError;
  const targetSelectionErrorText = isReplaceAction
    ? "Select confirmed profile material before replacing a version."
    : "Select confirmed profile material before extending a version.";
  const targetVersionErrorText = isReplaceAction
    ? "Choose the saved profile version to replace."
    : "Choose the saved profile version to extend.";
  const targetVersionHelperText = isReplaceAction
    ? "Replacing sets that saved version as the active version."
    : selectedSaveTargetVersion
      ? "Extending adds missing confirmed material to this saved version. It does not load or replace Studio selection."
      : activeProfileVersion
        ? "The active profile version is preselected when available."
        : "Choose the saved profile version that should receive the current selection.";

  const handleSaveActionChange = (nextAction: SaveVersionAction) => {
    setSaveAction(nextAction);
    setHasAttemptedCreate(false);
    setHasAttemptedReplace(false);
    setHasAttemptedExtend(false);
  };

  const handleCreateClick = () => {
    setHasAttemptedCreate(true);
    if (!canCreateVersion) return;

    void onCreateVersion();
  };

  const handleReplaceClick = () => {
    setHasAttemptedReplace(true);
    if (!canUseSaveTargetVersion || !canSaveVersion) return;

    void onUpdateVersion();
  };

  const handleExtendClick = () => {
    setHasAttemptedExtend(true);
    if (!canExtendVersion) return;

    void onExtendVersion();
  };

  return (
    <VStack align="stretch" gap="4">
      <GenericDrawerInfo
        description="Choose how the current confirmed Studio selection should update your saved profile versions."
        items={[
          { label: activeSaveActionLabel, colorPalette: "blue" },
          {
            label: canSaveVersion ? "Selection ready" : "Select profile first",
            colorPalette: canSaveVersion ? "teal" : "gray",
          },
        ]}
      />

      <SelectionSummary
        freeListThoughtCount={freeListThoughtCount}
        situationCount={selectedSituationCount}
        thoughtCount={selectedThoughtCount}
        hasSelectedSuggestions={hasSelectedSuggestions}
        printLanguage={printLanguage}
        profileFocusName={profileFocusName}
      />

      {hasVersions && (
        <VStack align="stretch" gap="2">
          <Text fontWeight="medium">Version action</Text>
          <GenericSegment<SaveVersionAction>
            activeState={activeSaveAction}
            setActiveState={handleSaveActionChange}
            size="sm"
            options={[
              { value: "create", label: "New" },
              { value: "replace", label: "Replace" },
              { value: "extend", label: "Extend" },
            ]}
          />
        </VStack>
      )}

      {activeSaveAction === "create" ? (
        <Field.Root invalid={!canSaveVersion || showVersionNameError}>
          <Field.Label>Version name</Field.Label>
          <Input
            value={versionName}
            onChange={(event) => {
              setVersionName(event.target.value);
            }}
            placeholder="e.g. Product manager profile"
          />
          {!canSaveVersion ? (
            <Field.ErrorText>
              Select confirmed profile material in Studio before saving a
              version.
            </Field.ErrorText>
          ) : showVersionNameError ? (
            <Field.ErrorText>Add a version name before saving.</Field.ErrorText>
          ) : (
            <Field.HelperText>
              Use a short label that makes this confirmed profile version easy
              to find.
            </Field.HelperText>
          )}
        </Field.Root>
      ) : (
        <VStack align="stretch" gap="2">
          <Text fontWeight="medium">{targetVersionTitle}</Text>
          <Text color="moveon.text.muted" fontSize="sm">
            {targetVersionDescription}
          </Text>
          <Field.Root
            invalid={showTargetSelectionError || showTargetVersionError}
          >
            <Field.Label>{targetVersionLabel}</Field.Label>
            <VersionSelector
              selectedVersions={selectedSaveTargetVersions}
              setSelectedVersions={setSelectedSaveTargetVersions}
              selectorEpoch={selectorEpoch}
              versionItems={versionItems}
              versions={versions}
              placeholder={targetVersionPlaceholder}
            />
            {showTargetSelectionError ? (
              <Field.ErrorText>
                {targetSelectionErrorText}
              </Field.ErrorText>
            ) : showTargetVersionError ? (
              <Field.ErrorText>
                {targetVersionErrorText}
              </Field.ErrorText>
            ) : (
              <Field.HelperText>{targetVersionHelperText}</Field.HelperText>
            )}
          </Field.Root>
          {isExtendAction && selectedSaveTargetVersion && canExtendVersion && (
            <Text color="moveon.text.muted" fontSize="sm">
              Only confirmed material missing from the selected version is
              added.
            </Text>
          )}
        </VStack>
      )}

      <Stack direction="row" justify="flex-end" gap="3" pt="2">
        <Button
          variant="ghost"
          onClick={onClose}
          aria-label="Cancel"
          title="Cancel"
          minW={{ base: "10", sm: "auto" }}
        >
          <GenericIcon icon="close" />
          <Text as="span" display={{ base: "none", sm: "inline" }}>
            Cancel
          </Text>
        </Button>
        {activeSaveAction === "create" ? (
          <Button
            colorPalette="blue"
            loading={savePending}
            disabled={!canSaveVersion}
            onClick={handleCreateClick}
            aria-label="Save version"
            title="Save version"
            minW={{ base: "10", sm: "auto" }}
          >
            <GenericIcon icon="save" />
            <Text as="span" display={{ base: "none", sm: "inline" }}>
              Save version
            </Text>
          </Button>
        ) : activeSaveAction === "replace" ? (
          <Button
            colorPalette="blue"
            loading={updatePending}
            disabled={!canSaveVersion}
            onClick={handleReplaceClick}
            aria-label="Replace selected version"
            title="Replace selected version"
            minW={{ base: "10", sm: "auto" }}
          >
            <GenericIcon icon="update" />
            <Text as="span" display={{ base: "none", sm: "inline" }}>
              Replace selected version
            </Text>
          </Button>
        ) : (
          <Button
            colorPalette="blue"
            loading={extendPending}
            disabled={!canSaveVersion}
            onClick={handleExtendClick}
            aria-label="Extend selected version"
            title="Extend selected version"
            minW={{ base: "10", sm: "auto" }}
          >
            <GenericIcon icon="save" />
            <Text as="span" display={{ base: "none", sm: "inline" }}>
              Extend selected version
            </Text>
          </Button>
        )}
      </Stack>
    </VStack>
  );
}
